import type { ReactNode } from "react";
import { CommunityImage } from "@circle-react/components/OnboardingComponents/CommunityImage";
import { ProgressSteps } from "@circle-react/components/OnboardingComponents/ProgressSteps";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useSetLocaleCookieFromParams } from "@circle-react/hooks/useSetLocaleCookieFromParams";
import { useStoreUtmParams } from "@circle-react/hooks/useStoreUtmParams";
import { PoweredByCircle } from "@circle-react-shared/PoweredByCircle";
import { BackgroundWrapper } from "./BackgroundWrapper";
import "./styles.css";

export interface OnboardingLayoutProps {
  background?: "dark" | "light" | "discover";
  children: ReactNode;
  community: any;
  isNarrow?: boolean;
  isVerticallyCenteredOnMobile?: boolean;
  progress?: string;
  shouldShowPoweredByCircle?: boolean;
  step?: string;
  subtitle?: ReactNode;
  title: ReactNode;
  withCircleBranding?: boolean;
}

export const OnboardingLayout = ({
  children,
  community,
  isNarrow = false,
  isVerticallyCenteredOnMobile = false,
  progress,
  shouldShowPoweredByCircle = false,
  step,
  subtitle,
  title,
  withCircleBranding = true,
  background = "dark",
}: OnboardingLayoutProps) => {
  const communitySlug = community?.slug;
  const currentStep = Number(progress?.split("/")[0]);
  const totalSteps = Number(progress?.split("/")[1]);
  const isSmScreen = useSmOrMdScreenMediaQuery();

  useSetLocaleCookieFromParams();
  useStoreUtmParams();

  const boxShadow =
    background == "light" && !isSmScreen
      ? "shadow-[0px_4px_6px_-2px_rgba(25,27,31,0.03),0px_12px_16px_-4px_rgba(25,27,31,0.08)]"
      : "";

  return (
    <>
      {/* eslint-disable-next-line sonarjs/jsx-no-leaked-render -- Disabled for plugin version update. Fix when possible. */}
      {progress && currentStep && totalSteps && (
        <div className="w-full">
          <ProgressSteps currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      )}
      <BackgroundWrapper
        background={background}
        withCircleBranding={withCircleBranding}
        isVerticallyCenteredOnMobile={isVerticallyCenteredOnMobile}
      >
        <div
          className={classNames("my-10 w-full", {
            "max-w-[450px]": isNarrow,
            "max-w-[520px]": !isNarrow,
          })}
        >
          <div
            className={`bg-primary border-primary m-auto flex w-full flex-col items-center p-6 py-0 md:rounded-2xl md:border md:p-12 md:py-10 ${boxShadow}`}
          >
            <CommunityImage
              community={community}
              withCircleBranding={withCircleBranding}
            />
            {step && (
              <p className="text-light -mb-5 mt-5 text-center text-xs">
                {step}
              </p>
            )}
            <h1 className="mt-4 text-center text-xl font-bold md:mt-6 md:text-2xl">
              {title}
            </h1>
            {subtitle && (
              <p className="text-light mt-1 text-center text-sm">{subtitle}</p>
            )}
            <div className="mt-10 w-full">{children}</div>
          </div>
          {shouldShowPoweredByCircle && (
            <div className="md:mt-2">
              <PoweredByCircle
                className="justify-center"
                communitySlug={communitySlug}
              />
            </div>
          )}
        </div>
      </BackgroundWrapper>
    </>
  );
};
